import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { type ReactElement, type ReactNode, useCallback, useMemo, useState } from 'react';

import {
  DetailItemCollapse,
  DetailItemContent,
  DetailItemExpandButton,
  DetailItemExpandCell,
  DetailItemLabel,
  DetailItemLabelContainer,
  DetailItemRow,
  DetailItemSpacer,
  DetailPanelRoot,
} from './DataGridDetailPanel.styled';

export type DataGridDetailPanelItemId = string | number;

export type DataGridDetailPanelItem = {
  id: DataGridDetailPanelItemId;
  /** Dynamic label (e.g. "Show 1 (...)", or any other text). */
  label: ReactNode;
  content: ReactNode;
  /** When false, item is omitted. Defaults to true. */
  visible?: boolean;
};

export type DataGridDetailPanelProps = {
  items: DataGridDetailPanelItem[];
  /** When false, the whole panel is not rendered. Defaults to true. */
  visible?: boolean;
  /**
   * Default collapsed state for uncontrolled mode.
   * `false` = expanded (default).
   */
  collapsed?: boolean;
  /**
   * Controlled expanded item ids.
   * Mirrors Data Grid `detailPanelExpandedRowIds`.
   * @see https://mui.com/x/react-data-grid/master-detail/#controlling-expanded-detail-panels
   */
  expandedItemIds?: DataGridDetailPanelItemId[];
  /**
   * Called when expanded item ids change.
   * Mirrors Data Grid `onDetailPanelExpandedRowIdsChange`.
   */
  onExpandedItemIdsChange?: (ids: DataGridDetailPanelItemId[]) => void;
  dataTestID?: string;
};

function toggleExpandedId(
  id: DataGridDetailPanelItemId,
  expandedIds: DataGridDetailPanelItemId[],
): DataGridDetailPanelItemId[] {
  if (expandedIds.includes(id)) {
    return expandedIds.filter((itemId) => itemId !== id);
  }
  return [...expandedIds, id];
}

/**
 * Expandable detail panel content for Data Grid master-detail rows.
 * @see https://mui.com/x/react-data-grid/master-detail/#controlling-expanded-detail-panels
 */
export function DataGridDetailPanel({
  items,
  visible = true,
  collapsed = false,
  expandedItemIds: controlledExpandedIds,
  onExpandedItemIdsChange,
  dataTestID = 'data-grid-detail-panel',
}: Readonly<DataGridDetailPanelProps>): ReactElement | null {
  const visibleItems = useMemo(() => items.filter((item) => item.visible !== false), [items]);

  const defaultExpandedIds = useMemo(
    () => (collapsed ? [] : visibleItems.map((item) => item.id)),
    [collapsed, visibleItems],
  );

  const [uncontrolledExpandedIds, setUncontrolledExpandedIds] =
    useState<DataGridDetailPanelItemId[]>(defaultExpandedIds);

  const isControlled = controlledExpandedIds !== undefined;
  const expandedIds = isControlled ? controlledExpandedIds : uncontrolledExpandedIds;

  const setExpandedIds = useCallback(
    (ids: DataGridDetailPanelItemId[]) => {
      if (!isControlled) {
        setUncontrolledExpandedIds(ids);
      }
      onExpandedItemIdsChange?.(ids);
    },
    [isControlled, onExpandedItemIdsChange],
  );

  const handleToggle = useCallback(
    (id: DataGridDetailPanelItemId) => {
      setExpandedIds(toggleExpandedId(id, expandedIds));
    },
    [expandedIds, setExpandedIds],
  );

  if (!visible || visibleItems.length === 0) {
    return null;
  }

  return (
    <DetailPanelRoot data-testid={dataTestID}>
      {visibleItems.map((item) => {
        const isExpanded = expandedIds.includes(item.id);

        return (
          <div key={item.id} data-testid={`detail-panel-item-${item.id}`}>
            <DetailItemRow>
              <DetailItemExpandCell>
                <DetailItemExpandButton
                  size="small"
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                  aria-expanded={isExpanded}
                  onClick={() => handleToggle(item.id)}
                  data-testid="expandable-section-caret"
                >
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </DetailItemExpandButton>
              </DetailItemExpandCell>
              <DetailItemLabelContainer>
                <DetailItemLabel data-testid={`detail-panel-label-${item.id}`}>{item.label}</DetailItemLabel>
              </DetailItemLabelContainer>
              <DetailItemSpacer />
            </DetailItemRow>
            <DetailItemCollapse in={isExpanded} timeout="auto" unmountOnExit={false}>
              <DetailItemContent data-testid={`detail-panel-content-${item.id}`}>{item.content}</DetailItemContent>
            </DetailItemCollapse>
          </div>
        );
      })}
    </DetailPanelRoot>
  );
}

export default DataGridDetailPanel;
